import React from 'react';
import { LoadingSpinner } from '@/shared/components';
import type { Product } from '@/features/products/types/products.types';
import type { ConfiguredService, ConfiguredAddOn } from '@/features/service-plans/types/services.types';
import type { BasketPricingState } from '../../hooks/useBasketPricing';
import type { PaymentSelection } from '../../types/schedule.types';
import { SchedulePreviewTable } from '../SchedulePreviewTable';
import { useDefaultScheduleSummary } from './useDefaultScheduleSummary';

interface DefaultScheduleSummaryCardProps {
    product: Product | null | undefined;
    configuredServices: ConfiguredService[];
    configuredAddOns: ConfiguredAddOn[];
    pricing: BasketPricingState;
    paymentSelection?: PaymentSelection;
    isServiceOrderUpdating?: boolean;
}

/** The confirmed selection when one exists, otherwise a live preview of the product's
 *  default_schedule method. Renders nothing when no schedule is computable. */
export const DefaultScheduleSummaryCard: React.FC<DefaultScheduleSummaryCardProps> = ({
    product,
    configuredServices,
    configuredAddOns,
    pricing,
    paymentSelection,
    isServiceOrderUpdating,
}) => {
    const { data, appliedDeposit, currency, anchor, isLoading } = useDefaultScheduleSummary({
        product,
        configuredServices,
        configuredAddOns,
        pricing,
        paymentSelection,
        isServiceOrderUpdating,
    });

    if (!data && !isLoading) {
        return null;
    }

    return (
        <div data-testid="config-payment-schedule">
            <h3 className="mb-3 text-base font-semibold text-mp-foreground">Payment Schedule</h3>
            {isLoading || !data ? (
                <LoadingSpinner message="Calculating schedule..." className="p-4" />
            ) : (
                <SchedulePreviewTable
                    schedule={data.schedule}
                    meta={data.meta}
                    numPayments={data.numPayments}
                    currency={currency}
                    depositRowConfirmed={appliedDeposit?.depositRowConfirmed === true}
                    anchorDate={anchor}
                    summaryOnly
                />
            )}
        </div>
    );
};
